// Browser build of EE2's host transport (slambench.com/ee2).
// The desktop app talks to an Electron main process over HTTP + WebSocket; here there is no
// main process: events stay in the page, the config lives in localStorage and GGG requests
// go through the site's own proxy route.
import type {
  IpcEvent,
  IpcEventPayload,
  UpdateInfo,
} from "@ipc/types";
import { shallowRef } from "vue";

const CONFIG_KEY = "ee2-web-config";
const PROXY_BASE = import.meta.env.VITE_PROXY_BASE || "/api/ee2-proxy";

class HostTransport {
  private evBus = new EventTarget();
  logs = shallowRef("");
  version = shallowRef("web");
  updateInfo = shallowRef<UpdateInfo>({ state: "initial" });

  async init() {
    // nothing to connect to: the page is its own host
  }

  selfDispatch(event: IpcEvent) {
    this.evBus.dispatchEvent(
      new CustomEvent(event.name, {
        detail: event.payload,
      }),
    );
  }

  /** Client -> "main process". Only config saving does anything in the browser. */
  sendEvent(event: IpcEvent) {
    if (event.name === "CLIENT->MAIN::save-config") {
      try {
        const { contents, isTemporary } = event.payload as {
          contents: string;
          isTemporary: boolean;
        };
        if (!isTemporary) localStorage.setItem(CONFIG_KEY, contents);
      } catch {
        // storage blocked: settings just won't persist
      }
    }
  }

  onEvent<Name extends IpcEvent["name"]>(
    name: Name,
    cb: (payload: IpcEventPayload<Name>) => void,
  ): AbortController {
    const controller = new AbortController();
    if (name.startsWith("MAIN->OVERLAY")) {
      return controller;
    }

    this.evBus.addEventListener(
      name,
      (e) => {
        cb((e as CustomEvent<IpcEventPayload<Name>>).detail);
      },
      { signal: controller.signal },
    );
    return controller;
  }

  async getConfig(): Promise<string | null> {
    try {
      return localStorage.getItem(CONFIG_KEY);
    } catch {
      return null;
    }
  }

  async importFile(file: File): Promise<string> {
    return file.name;
  }

  proxy: (typeof window)["fetch"] = async (url, init) => {
    return await window.fetch(`${PROXY_BASE}/${url as string}`, init);
  };

  get isElectron() {
    return false;
  }
}

export const MainProcess = new HostTransport();
export const Host = MainProcess;
